import { NotificationI } from "@/types/notifications";

const fireflyNotificationKeys = new Set([
  "firefly_ignited",
  "firefly_ignited_by_me",
]);
const groupedEventKeys = new Set([
  "tribute_left",
  ...Array.from(fireflyNotificationKeys),
]);
const passiveNotificationKeys = new Set([
  "tribute_submitted",
  "firefly_ignited_by_me",
]);

export type NotificationGroup = NotificationI & {
  notificationIds: number[];
  eventCount: number;
  quantity: number;
};

const localDayKey = (timestamp: string) => {
  const date = new Date(timestamp);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const eventQuantity = (notification: NotificationI) => {
  if (!fireflyNotificationKeys.has(notification.verb_key)) return 1;
  const quantity = Number(notification.verb_params.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
};

const groupKey = (notification: NotificationI) => {
  if (!groupedEventKeys.has(notification.verb_key)) {
    return `notification:${notification.id}`;
  }

  return [
    notification.verb_key,
    notification.target_id ?? "",
    notification.actor ?? notification.verb_params.actor_name ?? "",
    localDayKey(notification.timestamp),
  ].join(":");
};

export const groupNotifications = (
  notifications: NotificationI[]
): NotificationGroup[] => {
  const groups = new Map<string, NotificationGroup>();

  for (const notification of notifications) {
    const key = groupKey(notification);
    const isRead =
      notification.read || passiveNotificationKeys.has(notification.verb_key);
    const current = groups.get(key);
    if (!current) {
      groups.set(key, {
        ...notification,
        read: isRead,
        notificationIds: [notification.id],
        eventCount: 1,
        quantity: eventQuantity(notification),
      });
      continue;
    }

    current.notificationIds.push(notification.id);
    current.eventCount += 1;
    current.quantity += eventQuantity(notification);
    current.read = current.read && isRead;
    if (new Date(notification.timestamp) > new Date(current.timestamp)) {
      current.timestamp = notification.timestamp;
    }
  }

  return Array.from(groups.values()).sort(
    (left, right) =>
      new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime()
  );
};
